import { Immutable } from "immer";
import React from "react";
import { StarRating } from "tsi-common-react/src/components/StarRating/index";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import cardStyles from "../../styles/composable/card.module.scss";
import styles from "./index.module.scss";

// The card shape as serialized server-side (see server/cms/product_card.py).
type ProductCardData =
    templates.ProductListPageTemplate["categories"][number]["products"][number];

type IProps = Immutable<{
    product: ProductCardData;
    // Hook class applied to the card root.
    className?: string;
    // Whether to render the pre-title callout. When false, the pre-title block
    // is omitted and the image alt falls back to "".
    showPreTitle?: boolean;
    // Whether to render the aux-copy description block.
    showAuxCopy?: boolean;
}>;

export const ProductCard = (props: IProps): React.JSX.Element => {
    const {
        product,
        className,
        showPreTitle = true,
        showAuxCopy = true,
    } = props;

    return (
        <div
            className={concatClassNames([
                cardStyles.card,
                styles.productCardTempurpedic,
                "product-card",
                className,
            ])}
            data-product={product.id}
        >
            {product.product_schema_json_ld && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: product.product_schema_json_ld,
                    }}
                />
            )}
            <a href={product.link}>
                <img
                    src={product.image_url}
                    className={concatClassNames([
                        cardStyles.cardImage,
                        "responsive-img",
                    ])}
                    alt={showPreTitle ? product.pre_title : ""}
                    loading="lazy"
                    decoding="async"
                />
            </a>

            <div className={concatClassNames([cardStyles.cardContent])}>
                {showPreTitle && product.pre_title && (
                    <div
                        className={concatClassNames([
                            styles.productCardPreTitle,
                        ])}
                    >
                        {product.pre_title}
                    </div>
                )}

                <div
                    className={concatClassNames([
                        cardStyles.cardTitle,
                        cardStyles.productCardTitle,
                        styles.productCardTitleTempurpedic,
                    ])}
                >
                    <a href={product.link}>{product.title}</a>
                </div>

                {product.category_name && (
                    <div
                        className={concatClassNames([
                            styles.productCardCategory,
                        ])}
                    >
                        {product.category_name}
                    </div>
                )}

                <a href={product.reviews_url}>
                    <StarRating
                        rating={product.rating}
                        // A zero review count must hide the number entirely;
                        // StarRating renders any non-null number, so map 0 to null.
                        number={product.num_reviews || null}
                        className={styles.productCardRating}
                    />
                </a>

                {showAuxCopy && (
                    <div
                        className={concatClassNames([
                            cardStyles.productCardAuxCopy,
                        ])}
                        dangerouslySetInnerHTML={{
                            __html: product.description_html,
                        }}
                    />
                )}

                {product.price_exists && (
                    <div
                        className={concatClassNames([
                            styles.productCardPriceTempurpedic,
                        ])}
                    >
                        {product.price_retail ? (
                            <span>
                                <span className={styles.productCardPriceRetail}>
                                    {product.price_retail}
                                </span>
                                <span
                                    className={concatClassNames([
                                        "product-card__price__actual",
                                        "configurator__price__actual--highlight",
                                        styles.productCardPriceActualTempurpedic,
                                    ])}
                                >
                                    {product.price_actual}
                                </span>
                            </span>
                        ) : (
                            <span>
                                <span className="product-card__price__actual">
                                    {product.price_actual}
                                </span>
                            </span>
                        )}
                        {product.per_month && (
                            <a
                                href={product.finance_url}
                                className={concatClassNames([
                                    "product-card__installment",
                                ])}
                            >
                                or {product.per_month}/mo. for{" "}
                                {product.per_month_term_length} months
                                <sup>2</sup>
                            </a>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
